/* eslint-disable */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import './login.scss'
import darpanIcon from '../../Assets/images/svg/darpan_icon.svg'
import {
  WELCOME_TO_DARPAN,
  DARPAN_TOOL_TAGLINE,
} from '../../Constants/strings'
import lottie from 'lottie-web'
import PersonIcon from '@mui/icons-material/Person'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

export function LoginPage({ onSwitchToRegister, onLoginSuccess, onBackToHome, onForgotPassword }) {
  const [animationData, setAnimationData] = useState(null)
  const animationContainer = useRef(null)
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    import('../../Assets/images/json/dar_man_monitoring_dashboard.json').then(
      ({ default: animationData }) => {
        setAnimationData(animationData)
      },
    )
  }, [])

  useEffect(() => {
    if (animationData) {
      lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      })
    }
  }, [animationData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const result = await onLoginSuccess?.(formData.email, formData.password)

      if (result && result.success) {
        // Reset form
        setFormData({ email: '', password: '' })
      }
    } catch (error) {
      console.error('Login error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderWelcomText = () => {
    return (
      <div className="welcome-display-on-mobile">
        <img className="darpan-icon" src={darpanIcon} alt="Darpan Icon" />
        <div className="welcome-text-mobile">{WELCOME_TO_DARPAN}</div>
        <div className="tool-tagline-mobile">{DARPAN_TOOL_TAGLINE}</div>
      </div>
    )
  }

  const renderLoginForm = () => {
    return (
      <div className="login-form-container">
        {onBackToHome && (
          <button
            type="button"
            className="back-button"
            onClick={onBackToHome}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '1.5rem',
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '0.5rem',
              fontSize: '14px'
            }}
          >
            <ArrowBackIcon style={{ fontSize: '18px' }} />
            Back to Home
          </button>
        )}
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {errors.form && (
            <div className="error-message">
              {errors.form}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <PersonIcon className="input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <LockIcon className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label="Toggle password visibility"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </button>
            </div>
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <div className="forgot-password-link">
            {onForgotPassword && (
              <button
                type="button"
                className="link-button"
                onClick={onForgotPassword}
              >
                Forgot password?
              </button>
            )}
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>

          {onSwitchToRegister && (
            <div className="signup-link">
              Don't have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={onSwitchToRegister}
              >
                Sign up here
              </button>
            </div>
          )}
        </form>
      </div>
    )
  }

  return (
    <div className="container">
      <div className="left-panel">
        {renderWelcomText()}
        <div ref={animationContainer} className="left-panel-image"></div>
        <div className="login-display-on-mobile">
          {renderLoginForm()}
        </div>
      </div>
      <div className="right-panel">
        <div className="content-area">
          {renderWelcomText()}
          {renderLoginForm()}
        </div>
      </div>
    </div>
  )
}